import { supabase } from '@/core/database/supabase';
import type { EquipmentListData, EquipmentFilters, EquipmentStats } from '../types/equipmentList.types';

export const equipmentListService = {
  async getEquipmentList(companyId: string, filters: EquipmentFilters): Promise<EquipmentListData> {
    let query = supabase
      .from('equipment_assets')
      .select(`
        *,
        group:equipment_groups(id, name),
        assigned_to:user_profiles!equipment_assets_assigned_to_user_id_fkey(id, full_name, avatar_path),
        project:projects!equipment_assets_project_id_fkey(id, name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
    }

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status_code', filters.status);
    }

    if (filters.groupId) {
      query = query.eq('group_id', filters.groupId);
    }

    const { data, count, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;

    const items = (data || []).map(item => ({
      ...item,
      group_name: (item.group as any)?.name,
      assigned_to_name: (item.assigned_to as any)?.full_name,
      project_name: (item.project as any)?.name,
    }));

    const stats: EquipmentStats = {
      totalAssets: count || 0,
      availableCount: items.filter(i => i.status_code === 'available').length,
      inUseCount: items.filter(i => i.status_code === 'in_use' || i.status_code === 'assigned').length,
      maintenanceCount: items.filter(i => i.status_code === 'maintenance' || i.status_code === 'repairing').length,
      utilizationRate: count ? Math.round((items.filter(i => i.status_code !== 'available').length / count) * 100) : 0,
    };

    return { items, total: count || 0, stats };
  },

  async deleteEquipmentAsset(id: string, companyId: string): Promise<void> {
    const { error } = await supabase
      .from('equipment_assets')
      .delete()
      .eq('id', id)
      .eq('company_id', companyId);

    if (error) throw error;
  },

  async getGroups(companyId: string) {
    const { data, error } = await supabase
      .from('equipment_groups')
      .select('id, name, parent_id, level, path')
      .eq('company_id', companyId)
      .order('sort_order');
    if (error) throw error;
    return data || [];
  },
};

